import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L, { map } from "leaflet";
import { GeoJSON } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { useClickContext } from "../types/ClickContext";
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";
import { RasterResponse } from "../types/RasterRepsonse";
import colormap from "colormap";
import { useParameterContext } from "../types/ParameterContext"; // Import the custom hook for context
import { useMapSync } from "../types/MapSyncContext";
import MapEventsSync from "../types/MapEventsSync";
import { getHeatmap,getGeoJsonFile } from "../utils/restapi";
import { getHeatMapDiff } from "../utils/restapi";
import { useResizeObserver } from "../utils/useResizeObserver";
import { getColorScale,getGeoJsonValues } from "../utils/restapi"; // Import the getColorScale function
import ColorBarLegend from "./ColorBarLegend"; // Import the ColorBarLegend component
import { ColorContourResponse } from "../types/ColorContourRepsonse"; // Import the ColorContourResponse type
import { ColorControlPoint } from "../types/ColorControlPoint";

// Fix marker icon paths
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

function MapSizeFixer() {
  const map = useMap();

  useEffect(() => {
    setTimeout(() => {
      map.invalidateSize();
    }, 200); // slight delay ensures DOM is ready
  }, [map]);

  return null;
}

const parameterColorMap: Record<string, string> = {
  Precip: "viridis",
  Air_Temp: "hot",
  Elevation: "terrain",
  Wind: "cool",
  Relative_Humidity: "rainbow",
};

export interface MapViewProps {
  iscenario: string;
  parameter: string | null;
  time: string | null;

  startLoading: () => void;
  stopLoading: () => void;
}

const MapSyncHandler: React.FC = () => {
  const map = useMap();
  const { mapCenter, mapZoom } = useMapSync();

  useEffect(() => {
    const currentCenter = map.getCenter();
    const currentZoom = map.getZoom();

    if (
      currentCenter.lat !== mapCenter[0] ||
      currentCenter.lng !== mapCenter[1] ||
      currentZoom !== mapZoom
    ) {
      map.setView(mapCenter, mapZoom);
      // ✅ Invalidate size after layout change
      setTimeout(() => {
        map.invalidateSize();
      }, 10);
    }
  }, [map, mapCenter, mapZoom]);

  return null;
};

const MapView: React.FC<MapViewProps> = ({
  iscenario,
  parameter,
  time,
  startLoading,
  stopLoading,
}: MapViewProps) => {
  const { setClickData, clickData } = useClickContext(); // ⬅️ Use context here
  const [TimeSeriesReponse, setPlotData] = useState<any>(null);

  const { calcDiff, scenario, spatialAggregationName } = useParameterContext();

  const mapRef = useRef<L.Map | null>(null);
  const [raster, setRaster] = useState<RasterResponse | null>(null);
  const { mapCenter, mapZoom, setMapCenter, setMapZoom } = useMapSync();
  const containerRef = useResizeObserver(() => {
    if (mapRef.current) {
      mapRef.current.invalidateSize();
    }
  });
  const [colorContour, setColourContour] =
    useState<ColorContourResponse | null>(null); // Initialize colormap state");
  
  async function fetchColorContour(parameter: string) {
    try {
      const response = await getColorScale(parameter);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setColourContour(data as ColorContourResponse); // Set the color contour data
    } catch (error) {
      console.error("Invalid contour data:", error);
    }
  }

  useEffect(() => {
  if (parameter && time && iscenario) {
    const fetchAll = async () => {
      try {
        startLoading();

        // Wait for color contour data
        if(!calcDiff || scenario.length === 0 || scenario?.[0] === iscenario) {
          await fetchColorContour(parameter);
        }
        else {
          await fetchColorContour(parameter + "-diff");
        }
       

        // Fetch the heatmap or diff
        let response;
        if (!calcDiff || scenario.length === 0 || scenario?.[0] === iscenario) {
          response = await getHeatmap(iscenario, parameter, time);
        } else {
          response = await getHeatMapDiff(iscenario, scenario[0], parameter, time);
        }

        if (!response.ok) {
          throw new Error("Failed to fetch heatmap");
        }

        const rasterData = await response.json();
        if (rasterData) {
          setRaster(rasterData);
        } else {
          console.error("Invalid raster data format:", rasterData);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        stopLoading();
      }
    };

    fetchAll();
  }
}, [scenario, parameter, time, calcDiff]);

  return (
    <div ref={containerRef} className="w-full-20 h-full z-0">
      <MapContainer
        center={mapCenter}
        zoom={mapZoom}
        className="w-full h-full"
        style={{ height: "100%", width: "100%" }}
      >
        <MapEventsSync setMapCenter={setMapCenter} setMapZoom={setMapZoom} />
        <MapSyncHandler />

        {clickData && (
          <div className="fixed bottom-0 right-0 bg-white p-3 m-4 shadow-lg border rounded z-[1000]">
            <p className="text-sm font-medium">Clicked Location</p>
            <p className="text-xs text-gray-700">
              Lat: {clickData.lat.toFixed(5)}, Lon: {clickData.lon.toFixed(5)}
            </p>
          </div>
        )}

        {clickData && (
          <Marker position={[clickData.lat, clickData.lon]}>
            <Popup>
              Lat: {clickData.lat.toFixed(4)}, Lon: {clickData.lon.toFixed(4)}
            </Popup>
          </Marker>
        )}

        <MapSizeFixer />
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        {spatialAggregationName ? (
          <>
          <SpatialLayer
            spatialAggregationName={spatialAggregationName}
            scenario={iscenario}
            parameter={parameter }
            selectedTime={time }
            colorContour={
                colorContour ? colorContour : ({} as ColorContourResponse)
              }
            onMapClick={(lat, lon, featureTypeName, featureProperties) => {
              setClickData({ lat, lon, featureTypeName, featureProperties });
            }}
          />
          <ColorBarLegend
              min={colorContour ? colorContour.min : 0}
              max={colorContour ? colorContour.max : 100}
              units={raster ? raster.units : ""}
              colormapName={
                colorContour ? colorContour : ({} as ColorContourResponse)
              } // Use the colorContour state directly
            />
            </>
        ) : (
          <>
            {raster && (
              <RasterOverlay
                raster={raster}
                onMapClick={(lat, lon) => {setClickData({ lat, lon });}}
              colorContour={
                colorContour ? colorContour : ({} as ColorContourResponse)
              } // Pass the colorContour state directly
            />
          )}
        
          {raster && ( 
            <ColorBarLegend
              min={colorContour ? colorContour.min : raster.min}
              max={colorContour ? colorContour.max : raster.max}
              units={raster.units}
              colormapName={
                colorContour ? colorContour : ({} as ColorContourResponse)
              } // Use the colorContour state directly
            />
          )}
          </>

        )
        }
          
      </MapContainer>
    </div>
  );
};
interface SpatialLayerProps {
  spatialAggregationName: string | null;
  scenario: string | null;
  parameter: string | null;
  selectedTime: string | null;
  colorContour: ColorContourResponse; // Add colorContour prop
  onMapClick: (lat: number, lon: number, featureTypeName?: string, featureProperties?: { [key: string]: any }) => void;
}

const SpatialLayer: React.FC<SpatialLayerProps> = ({
  spatialAggregationName,
  scenario,
  parameter,
  selectedTime,
  colorContour,
  onMapClick,
}) => {
  const [geoData, setGeoData] = useState<any>(null);
  const [valueMap, setValueMap] = useState<Record<number, number>>({});
  const [highlightedObjectId, setHighlightedObjectId] = useState<number | null>(null);
  const map = useMap(); // Ensure you're using useMap only inside the context of MapContainer
   
  useEffect(() => {
    if (!spatialAggregationName || !scenario || !parameter || !selectedTime) {
      setGeoData(null);
      setValueMap({});
      return;
    }

    getGeoJsonFile(spatialAggregationName)
      .then((res) => res.json())
      .then((geojson) => {
        setGeoData(geojson);
        return getGeoJsonValues(
          scenario,
          parameter,
          spatialAggregationName,
          selectedTime
        );
      })
      .then((res) => res.json()).then((heatmapJson) => {
        const byteString = atob(heatmapJson.data);
        const byteArray = new Uint8Array(byteString.length);  
        for (let i = 0; i < byteString.length; i++) {
          byteArray[i] = byteString.charCodeAt(i);
        }
        const floatArray = new Float32Array(byteArray.buffer);

        const data: number[] = Array.from(floatArray);
        

        const valueMap: Record<number, number> = {};
         data.forEach((val, idx) => {
           const idx2 = idx+1; // Assuming OBJECTID starts from 1
           valueMap[idx2] = val;
         });
        setValueMap(valueMap);
      })
      .catch((err) => {
        console.error("Error loading spatial data:", err);
        setGeoData(null);
      });
    return () => {
      map.off("click");
      setGeoData(null);
      setValueMap({});
    };
  }, [spatialAggregationName, scenario, parameter, selectedTime]);

  const getColor = (value: number): string => {
    if (
      !colorContour ||
      !colorContour.colors ||
      colorContour.colors.length === 0 ||
      isNaN(value)
    ) {
      return "#999"; // fallback gray
    }

    const scale = (val: number): number =>
      Math.min(
        255,
        colorContour.max - colorContour.min === 0
          ? 0
          : Math.max(
              0,
              ((val - colorContour.min) / (colorContour.max - colorContour.min)) * 255
            )
      );

    const colors = colormap({
      colormap: colorContour.colors as ColorControlPoint[],
      nshades: 256,
      format: "rgba",
      alpha: 1,
    });

    const index = Math.round(scale(value));
    let r: number, g: number, b: number;
    const color = colors[index] ?? [128, 128, 128];

    if (Array.isArray(color)) {
      [r, g, b] = color;
    } else if (typeof color === "string") {
      // Parse "rgba(r,g,b,a)" string
      const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
      if (match) {
        r = parseInt(match[1], 10);
        g = parseInt(match[2], 10);
        b = parseInt(match[3], 10);
      } else {
        r = g = b = 128;
      }
    } else {
      r = g = b = 128;
    }

    const toHex = (n: number) => n.toString(16).padStart(2, "0");
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  };
  const { transparency } = useParameterContext();
  // Style for non-selected polygons
  const styleFeature = (feature: any) => {
    const objectId = feature.properties?.OBJECTID;
    const value = valueMap[objectId];
    return {
      fillColor: getColor(value),
      weight: 1,
      opacity: 1,
      color: "black",
      fillOpacity: ((100 - (transparency ?? 0)) / 100),
    };
  };

  // Style for selected polygon (highlight)
  const styleSelected = (feature: any) => {
    const objectId = feature.properties?.OBJECTID;
    const value = valueMap[objectId];
    return {
      fillColor: getColor(value), // bright yellow
      weight: 7,
      opacity: 1,
      color: "#FF0000", // thick red border
      fillOpacity: ((100 - (transparency ?? 0)) / 100),
    };
  };

  // Click handler for both layers
  const onEachFeature = (feature: any, layer: L.Layer) => {
    layer.on("click", (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      setHighlightedObjectId(feature.properties?.OBJECTID ?? null);
      onMapClick(lat, lng, spatialAggregationName ?? undefined, feature.properties);
    });
  };

  // Split features for selection layer logic
  let nonSelectedGeoData = null;
  let selectedGeoData = null;
  if (geoData && geoData.features && geoData.features.length > 0) {
    const selectedFeature = geoData.features.find(
      (f: any) => f.properties?.OBJECTID === highlightedObjectId
    );
    const nonSelectedFeatures = geoData.features.filter(
      (f: any) => f.properties?.OBJECTID !== highlightedObjectId
    );
    // Build GeoJSON objects for each layer
    nonSelectedGeoData = {
      ...geoData,
      features: nonSelectedFeatures,
    };
    selectedGeoData = selectedFeature
      ? {
          ...geoData,
          features: [selectedFeature],
        }
      : null;
  }

  // Point rendering logic (shared)
  const pointToLayer = (feature: any, latlng: L.LatLng) => {
    const objectId = feature.properties?.OBJECTID;
    // Highlight selected polygon's point as yellow
    if (objectId === highlightedObjectId) {
      return L.marker(latlng, {
        icon: L.divIcon({
          className: "custom-yellow-square",
          iconSize: [10, 10],
          html: `<div style=\"width:15px;height:15px;background:yellow;border:2px solid #FF0000;\"></div>`,
        }),
        interactive: true,
      });
    }
    // Non-selected points as blue
    return L.marker(latlng, {
      icon: L.divIcon({
        className: "custom-blue-square",
        iconSize: [10, 10],
        html: `<div style=\"width:10px;height:10px;background:blue;\"></div>`,
      }),
      interactive: true,
    });
  };

  
  return geoData ? (
    <>
      {/* Main layer: all polygons except selected */}
      <GeoJSON
        key={highlightedObjectId ? `main-${highlightedObjectId}` : "main"}
        data={nonSelectedGeoData}
        style={styleFeature}
        onEachFeature={onEachFeature}
        pointToLayer={pointToLayer}
      />
      {/* Selection layer: selected polygon only, above main layer */}
      {selectedGeoData && (
        <GeoJSON
          key={`selected-${highlightedObjectId}`}
          data={selectedGeoData}
          style={styleSelected}
          onEachFeature={onEachFeature}
          pointToLayer={pointToLayer}
        />
      )}
    </>
  ) : null;



};


const MapComponent: React.FC = () => {
  const map = useMap(); // Now we are inside MapContainer, so we can use useMap here
  const { mapCenter, mapZoom } = useMapSync();

  useEffect(() => {
    // Set the initial map center and zoom after the map has been initialized
    map.setView(mapCenter, mapZoom);
  }, [map]);

  return (
    <>
      
    </>
  );
};

interface RasterOverlayProps {
  raster: RasterResponse;
  onMapClick: (lat: number, lon: number) => void;
  colorContour: ColorContourResponse; // Add colorContour prop
}

const RasterOverlay: React.FC<RasterOverlayProps> = ({
  raster,
  onMapClick,
  colorContour,
}) => {
  const map = useMap(); // Ensure you're using useMap only inside the context of MapContainer
  const { transparency } = useParameterContext();
  useEffect(() => {
    if (!raster) return;

    const canvas = document.createElement("canvas");
    canvas.width = raster.width;
    canvas.height = raster.height;
    const ctx = canvas.getContext("2d");

    if (!ctx) {
      console.error("Failed to get 2D canvas context");
      return;
    }

    const imageData = ctx.createImageData(raster.width, raster.height);
    const byteString = atob(raster.data);
    const byteArray = new Uint8Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteArray[i] = byteString.charCodeAt(i);
    }

    const floatArray = new Float32Array(byteArray.buffer);

    const scale = (value: number): number =>
      Math.min(
        255,
        colorContour.max - colorContour.min == 0
          ? 0
          : Math.max(
              0,
              ((value - colorContour.min) /
                (colorContour.max - colorContour.min)) *
                255
            )
      );

    const colors = colormap({
      colormap:  colorContour.colors,
      nshades: 256,
      format: "rgba",
      alpha: 1,
    });
    for (let i = 0; i < floatArray.length; i++) {
      const val = floatArray[i];
      const offset = i * 4;
      
      if(!colorContour) {
        imageData.data[offset + 0] = Number(0);
        imageData.data[offset + 1] = Number(0);
        imageData.data[offset + 2] = Number(0);
        imageData.data[offset + 3] = Number(0);   
      }
      else if (val == raster.noDataValue || val < colorContour?.min) {
        imageData.data[offset + 3] = 0; // transparent pixel for "no data"
        continue;
      }
      

      else {
        if(!colorContour) {
          debugger
        }
        const color = colors[Math.round(scale(val))];
        if(!color) {
          imageData.data[offset + 0] = Number(255);
          imageData.data[offset + 1] = Number(0);
          imageData.data[offset + 2] = Number(0);
          imageData.data[offset + 3] = Number(255);
        }
        else {
          // Use the color from the colormap
          imageData.data[offset + 0] = Number(color[0]);
          imageData.data[offset + 1] = Number(color[1]);
          
          imageData.data[offset + 3] = Math.round((Number(color[3]) * 255)*((100-(transparency??0))/100)); // convert alpha to 0–255
          imageData.data[offset + 2] = Number(color[2]);
        }
      }
      
    }

    ctx.putImageData(imageData, 0, 0);
    const imageUrl = canvas.toDataURL();

    const bounds: L.LatLngBoundsExpression = [
      [32.515625 - 0.14694, -124.421875],
      [43.296875 - 0.14694, -113.890625],
    ];
    const overlay = L.imageOverlay(imageUrl, bounds).addTo(map);

    map.on("click", (e: L.LeafletMouseEvent) => {
      const { lat, lng } = e.latlng;
      onMapClick(lat, lng);
    });

    return () => {
      map.off("click");
      map.removeLayer(overlay);
    };
  }, [raster, map,transparency]);

  return null;
};

export default MapView;
